// OverlayManager — Manages all native chart overlays (price lines, markers)
// Pure JS class — no React dependencies

import { OVERLAY_COLORS, OVERLAY_STYLES, MARKER_COLORS } from '../../../config/tradingConstants';

export default class OverlayManager {
  constructor(candleSeries, markerPlugin) {
    this.series = candleSeries;
    this.markerPlugin = markerPlugin;
    this.priceLines = new Map(); // id → priceLine reference
    this.markers = [];           // sorted marker array
  }

  // ── Price Line Helpers ──────────────────────────────────────

  _upsertPriceLine(id, options) {
    this._removePriceLine(id);
    if (!this.series || options.price == null || !isFinite(options.price)) return;
    try {
      const line = this.series.createPriceLine(options);
      this.priceLines.set(id, line);
    } catch (e) {
      // Series may have been removed
    }
  }

  _removePriceLine(id) {
    const line = this.priceLines.get(id);
    if (line && this.series) {
      try { this.series.removePriceLine(line); } catch (e) { /* already removed */ }
    }
    this.priceLines.delete(id);
  }

  _removeGroup(prefix) {
    const toRemove = [];
    this.priceLines.forEach((_, id) => {
      if (id.startsWith(prefix)) toRemove.push(id);
    });
    toRemove.forEach(id => this._removePriceLine(id));
  }

  // ── Signal Lines ────────────────────────────────────────────

  setEntryLine(price) {
    this._upsertPriceLine('entry', {
      price,
      color: OVERLAY_COLORS.entry,
      title: `Entry ${price}`,
      ...OVERLAY_STYLES.entry,
    });
  }

  setStopLossLine(price) {
    this._upsertPriceLine('stopLoss', {
      price,
      color: OVERLAY_COLORS.stopLoss,
      title: `SL ${price}`,
      ...OVERLAY_STYLES.stopLoss,
    });
  }

  setTargetLine(price) {
    this._upsertPriceLine('target', {
      price,
      color: OVERLAY_COLORS.target,
      title: `Target ${price}`,
      ...OVERLAY_STYLES.target,
    });
  }

  removeSignalLines() {
    this._removePriceLine('entry');
    this._removePriceLine('stopLoss');
    this._removePriceLine('target');
  }

  // ── ORB Zone ────────────────────────────────────────────────

  setORBZone(high, low) {
    this._upsertPriceLine('orbHigh', {
      price: high,
      color: OVERLAY_COLORS.orbHigh,
      title: `ORB High ${high}`,
      ...OVERLAY_STYLES.orbHigh,
    });
    this._upsertPriceLine('orbLow', {
      price: low,
      color: OVERLAY_COLORS.orbLow,
      title: `ORB Low ${low}`,
      ...OVERLAY_STYLES.orbLow,
    });
  }

  removeORBZone() {
    this._removePriceLine('orbHigh');
    this._removePriceLine('orbLow');
  }

  // ── Expected Move Bands ─────────────────────────────────────

  setExpectedMoveBands(upper, lower) {
    this._upsertPriceLine('expectedUpper', {
      price: upper,
      color: OVERLAY_COLORS.expectedUpper,
      title: `EM+ ${upper}`,
      ...OVERLAY_STYLES.expectedUpper,
    });
    this._upsertPriceLine('expectedLower', {
      price: lower,
      color: OVERLAY_COLORS.expectedLower,
      title: `EM- ${lower}`,
      ...OVERLAY_STYLES.expectedLower,
    });
  }

  removeExpectedMoveBands() {
    this._removePriceLine('expectedUpper');
    this._removePriceLine('expectedLower');
  }

  // ── Prev Day High/Low ──────────────────────────────────────

  setPrevDayHighLow(high, low) {
    this._upsertPriceLine('prevDayHigh', {
      price: high,
      color: OVERLAY_COLORS.prevDayHigh,
      title: `PDH ${high}`,
      ...OVERLAY_STYLES.prevDayHigh,
    });
    this._upsertPriceLine('prevDayLow', {
      price: low,
      color: OVERLAY_COLORS.prevDayLow,
      title: `PDL ${low}`,
      ...OVERLAY_STYLES.prevDayLow,
    });
  }

  removePrevDayHighLow() {
    this._removePriceLine('prevDayHigh');
    this._removePriceLine('prevDayLow');
  }

  // ── Weekly High/Low ─────────────────────────────────────────

  setWeeklyHighLow(high, low) {
    this._upsertPriceLine('weeklyHigh', {
      price: high,
      color: OVERLAY_COLORS.weeklyHigh,
      title: `WH ${high}`,
      ...OVERLAY_STYLES.weeklyHigh,
    });
    this._upsertPriceLine('weeklyLow', {
      price: low,
      color: OVERLAY_COLORS.weeklyLow,
      title: `WL ${low}`,
      ...OVERLAY_STYLES.weeklyLow,
    });
  }

  removeWeeklyHighLow() {
    this._removePriceLine('weeklyHigh');
    this._removePriceLine('weeklyLow');
  }

  // ── Markers ─────────────────────────────────────────────────

  addBuyMarker(time, text = 'BUY') {
    this.markers.push({
      time,
      text,
      ...MARKER_COLORS.buy,
    });
    this._applyMarkers();
  }

  addSellMarker(time, text = 'SELL') {
    this.markers.push({
      time,
      text,
      ...MARKER_COLORS.sell,
    });
    this._applyMarkers();
  }

  setTradeMarkers(trades) {
    if (!trades || trades.length === 0) {
      this.clearMarkers();
      return;
    }
    this.markers = trades.map(t => ({
      time: t.time,
      text: t.text || (t.type === 'BUY' ? 'B' : 'S'),
      ...(t.type === 'BUY' ? MARKER_COLORS.buy : MARKER_COLORS.sell),
    }));
    this._applyMarkers();
  }

  clearMarkers() {
    this.markers = [];
    this._applyMarkers();
  }

  _applyMarkers() {
    if (!this.markerPlugin) return;
    try {
      // Sort markers by time (required by lightweight-charts)
      const sorted = [...this.markers].sort((a, b) => a.time - b.time);
      this.markerPlugin.setMarkers(sorted);
    } catch (e) {
      // Plugin may have been detached
    }
  }

  // ── Bulk Operations ─────────────────────────────────────────

  // Apply a signal from the algo engine — sets entry/SL/target + entry marker
  applySignal(signal, entryTime) {
    if (!signal) {
      this.removeSignalLines();
      return;
    }
    if (signal.entry) this.setEntryLine(signal.entry);
    if (signal.stop_loss || signal.sl) this.setStopLossLine(signal.stop_loss || signal.sl);
    if (signal.target) this.setTargetLine(signal.target);

    if (entryTime && signal.entry) {
      this.addBuyMarker(entryTime, signal.strategy || 'ENTRY');
    }
  }

  // ── Lifecycle ───────────────────────────────────────────────

  removeAll() {
    // Remove all price lines
    this.priceLines.forEach((line, id) => {
      if (this.series) {
        try { this.series.removePriceLine(line); } catch (e) { /* ok */ }
      }
    });
    this.priceLines.clear();
    this.clearMarkers();
  }

  rebind(newSeries, newMarkerPlugin) {
    this.removeAll();
    this.series = newSeries;
    this.markerPlugin = newMarkerPlugin;
  }
}
